import React, { useState, ChangeEvent, FormEvent } from 'react';
import { SimulationData } from '../App';

interface BottomCTAProps {
  onStartSimulation: (data: SimulationData) => void;
  title?: string;
  subtitle?: string;
}

interface LeadFormData {
  country: string;
  city: string;
  email: string;
  [key: string]: string | number | undefined;
}

interface FormErrors {
  country?: string;
  city?: string;
  email?: string;
}

const BottomCTA: React.FC<BottomCTAProps> = ({ onStartSimulation, title, subtitle }) => {
  const [formData, setFormData] = useState<LeadFormData>({ country: '', city: '', email: '' });
  const [errors, setErrors] = useState<FormErrors>({});

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const newErrors: FormErrors = {};
    if (!formData.country) newErrors.country = 'Please select a country';
    if (!formData.city.trim()) newErrors.city = 'Please enter a city';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    onStartSimulation(formData);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const defaultTitle = "Know before you go.<br/><em>Simulate first.</em>";
  const defaultSubtitle = "Submit your target market and receive a complete 12-month simulation — revenue, costs, break-even timing, and a multi-market comparison.";

  return (
    <section className="bottom-cta">
      <div className="bottom-cta-inner">
        <h2 dangerouslySetInnerHTML={{ __html: title || defaultTitle }}></h2>
        <p className="sub">{subtitle || defaultSubtitle}</p>
        <p style={{ marginTop: '16px', marginBottom: '40px', fontFamily: "'DM Mono', monospace", fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 600 }}>Simulation Delivered Within 72 Hours</p>
        <div className="delivery-badge-dark">
          <div className="dot"></div>
          <span>Simulation Delivered Within 72 Hours</span>
        </div>
        <div className="bottom-form">
          <h3>Get your free simulation</h3>
          <p className="form-sub">Tell us your target market. We deliver your market simulation within 72 hours.</p>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Target Country *</label>
                <select name="country" value={formData.country} onChange={handleChange} style={errors.country ? { borderColor: '#c0392b' } : {}}>
                  <option value="">Select a Country</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Spain">Spain</option>
                  <option value="Italy">Italy</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Japan">Japan</option>
                </select>
                {errors.country && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.country}</span>}
              </div>
              <div className="form-group">
                <label>Target City *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g., New York" style={errors.city ? { borderColor: '#c0392b' } : {}} />
                {errors.city && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.city}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Your Work Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g., you@company.com" style={errors.email ? { borderColor: '#c0392b' } : {}} />
              {errors.email && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
            </div>
            <button type="submit" className="form-submit">Start My Simulation →</button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BottomCTA;
